import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useContext } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import { AuctionIcon, Alert01Icon, ArrowRightIcon, Tick02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { MainContainer, MainHeaderBackButton, MainScrollArea } from '@/app/ui/components/main';
import { H1, H2 } from '@/app/ui/components/heading';
import { Spinner } from '@/app/ui/components/spinner';
import { useBidQualitySectionQuery } from '@/app/hooks/queries/useBidQuality';
import { Card } from '@/app/ui/components/card';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import { Badge } from '@/app/ui/components/badge';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
  Sheet,
  SheetPopup,
  SheetHeader,
  SheetTitle,
  SheetPanel,
  SheetXClose,
} from '@/app/ui/components/sheet';
import { Button } from '@/app/ui/components/button';
import { BidQualityStatement } from '@/app/types/models';
import { getAssessmentLabelKey, getBidAssessmentColor } from '@/app/components/features/bids/utils';
import CitationListItem from '@/app/components/shared/Citations/CitationListItem';
import FeedbackCard from '@/app/components/shared/Feedback/FeedbackCard';
import { FeedbackStatementTypeEnum, queryKeys } from '@/app/utils/constants';
import useGetDocumentById from '@/app/components/shared/EntityDocuments/useGetDocumentById';
import { AppContext } from '@/app/appContext';
import { CircularProgress } from '@/app/ui/components/circular-progress';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { cn } from '@/app/utils/helpers';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/methodology/$categoryId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const tGeneral = useTranslations('general');
  const t = useTranslations('pages.bids');
  const tBidQuality = useTranslations('components.bidQuality');
  const { bidId, tenderPackageId, projectId, categoryId } = Route.useParams();
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState<BidQualityStatement | undefined>();
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();

  const { data, isLoading } = useBidQualitySectionQuery({
    bidId,
    sectionId: categoryId,
  });

  useEffect(() => {
    if (data && selectedStatement) {
      const correspondingStatement = data.statements.find(
        (stmt) => stmt.id === selectedStatement.id,
      );
      if (correspondingStatement) {
        setSelectedStatement(correspondingStatement);
      }
    }
  }, [data, selectedStatement]);

  const handleOpenSheet = (stmt: BidQualityStatement, index: number) => {
    setSelectedStatement(stmt);
    setSelectedIndex(index);
    setIsSheetOpen(true);
  };

  const currentIndex = selectedStatement
    ? (data?.statements.findIndex((s) => s.id === selectedStatement.id) ?? -1)
    : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && data && currentIndex < data.statements.length - 1;

  const handlePrev = () => {
    if (hasPrev && data) {
      const newIndex = currentIndex - 1;
      setSelectedIndex(newIndex);
      setSelectedStatement(data.statements[newIndex]);
    }
  };

  const handleNext = () => {
    if (hasNext && data) {
      const newIndex = currentIndex + 1;
      setSelectedIndex(newIndex);
      setSelectedStatement(data.statements[newIndex]);
    }
  };

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {t('title')}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <span className="line-clamp-1">{bid.bidAdditionalName || bid.companyName}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/methodology"
                params={{ projectId, tenderPackageId, bidId }}
              >
                {tBidQuality('cards.methodology.title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{tBidQuality('labels.section')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
      </Header>
      <MainScrollArea>
        {isLoading ? (
          <Spinner kind="inset" />
        ) : (
          data && (
            <MainContainer>
              <H1 className="mb-6">
                <MainHeaderBackButton />
                {data.title}
              </H1>
              <Card className="mb-6">
                <div className="flex flex-col">
                  <p className="pb-2 text-xs text-muted">
                    {tBidQuality('categoryDetails.detailsCard.description')}
                  </p>
                  <H2 className="py-0.5 text-xl">{data.title}</H2>
                  <div className="flex items-center gap-5 mt-5">
                    <CircularProgress
                      max={10}
                      getValueText={(value) => value.toFixed(1)}
                      value={data.totalScore}
                      size={125}
                      label={tBidQuality('labels.score')}
                    />
                    <div className="w-64 flex flex-col gap-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>{tBidQuality('labels.good')}</span>
                        <span className="font-medium">{data.good}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>{tBidQuality('labels.needsImprovement')}</span>
                        <span className="font-medium">{data.needsImprovement}</span>
                      </div>
                      <div className="flex items-center justify-between border-t pt-1 text-sm text-muted">
                        <span className="font-medium">{tBidQuality('labels.total')}</span>
                        <span className="font-medium">{data.good + data.needsImprovement}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
              <div className="space-y-3">
                {data.statements.map((statement, idx) => {
                  const assessmentLabel = tBidQuality(
                    `assessmentLabels.${getAssessmentLabelKey(statement.score)}`,
                  );
                  const assessmentDisplayValue =
                    statement.score !== undefined
                      ? `${statement.score.toFixed(1)} ${assessmentLabel}`
                      : undefined;

                  return (
                    <Card
                      key={statement.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleOpenSheet(statement, idx)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <p className="flex-1 text-sm">{statement.statement}</p>
                        <div className="flex items-center gap-2 shrink-0">
                          {assessmentDisplayValue ? (
                            <Badge
                              variant="secondary"
                              style={{ color: getBidAssessmentColor(statement.score) }}
                            >
                              {assessmentDisplayValue}
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="gap-1">
                              <HugeiconsIcon icon={Alert01Icon} className="size-3" />
                              {tBidQuality('assessmentLabels.missing')}
                            </Badge>
                          )}
                          {statement.isPositiveFeedback !== null && (
                            <Badge
                              variant={statement.isPositiveFeedback ? 'success' : 'destructive'}
                            >
                              {statement.isPositiveFeedback ? (
                                <HugeiconsIcon icon={Tick02Icon} className="size-3" />
                              ) : (
                                <HugeiconsIcon icon={Alert01Icon} className="size-3" />
                              )}
                            </Badge>
                          )}
                          <HugeiconsIcon icon={ArrowRightIcon} className="size-5 text-muted" />
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </MainContainer>
          )
        )}
      </MainScrollArea>
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetPopup side="right">
          <SheetHeader>
            <SheetTitle>
              {tBidQuality('categoryDetails.drawer.header')}
              <SheetXClose />
            </SheetTitle>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={handlePrev} disabled={!hasPrev}>
                {tGeneral('previous')}
              </Button>
              <Button variant="secondary" size="sm" onClick={handleNext} disabled={!hasNext}>
                {tGeneral('next')}
              </Button>
            </div>
          </SheetHeader>
          <SheetPanel>
            {selectedStatement && (
              <StatementDetails statement={selectedStatement} sectionId={categoryId} />
            )}
          </SheetPanel>
        </SheetPopup>
      </Sheet>
    </>
  );
}

function StatementDetails({
  statement,
  sectionId,
}: {
  statement: BidQualityStatement;
  sectionId: string;
}) {
  const tBidQuality = useTranslations('components.bidQuality');
  const { bidId, projectId } = Route.useParams();
  const { userMeta } = useContext(AppContext);
  const { mutateAsync } = useGetDocumentById();

  const scoreColor = getBidAssessmentColor(statement.score);
  const formattedSuggestion = statement.suggestion.replace(/\b(REPLACE|ADD|AFTER)\b/g, '**$1**');

  const [bidDocTitle, setBidDocTitle] = useState<string | undefined>();
  const [rfpDocTitle, setRfpDocTitle] = useState<string | undefined>();

  useEffect(() => {
    let isMounted = true;
    const fetchNames = async () => {
      try {
        if (statement.bidDocumentId) {
          const bidDoc = await mutateAsync({
            projectId,
            organizationId: userMeta.organizationId ?? '',
            documentId: statement.bidDocumentId,
          });
          if (isMounted) setBidDocTitle(bidDoc.name);
        }
        if (statement.rfpDocumentId) {
          const rfpDoc = await mutateAsync({
            projectId,
            organizationId: userMeta.organizationId ?? '',
            documentId: statement.rfpDocumentId,
          });
          if (isMounted) setRfpDocTitle(rfpDoc.name);
        }
      } catch {
        // ignore
      }
    };
    if (statement.bidDocumentId || statement.rfpDocumentId) {
      fetchNames();
    }
    return () => {
      isMounted = false;
    };
  }, [
    statement.bidDocumentId,
    statement.rfpDocumentId,
    mutateAsync,
    projectId,
    userMeta.organizationId,
  ]);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold">
          {tBidQuality('categoryDetails.drawer.bidStatement')}
        </h4>
        <MarkdownTextBlock sectionBody={statement.statement} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold">
          {tBidQuality('categoryDetails.drawer.bidSourceDocument')}
        </h4>
        <CitationListItem
          withAccent
          showIcon={false}
          documentId={statement.bidDocumentId}
          content={statement.bidSource}
          source={statement.bidSource}
          title={
            bidDocTitle ||
            statement.bidSource ||
            tBidQuality('categoryDetails.drawer.bidSourceDocument')
          }
        />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold">{tBidQuality('categoryDetails.drawer.rfpSource')}</h4>
        <CitationListItem
          withAccent
          showIcon={false}
          documentId={statement.rfpDocumentId}
          content={statement.rfpSource}
          source={statement.rfpSource}
          title={
            rfpDocTitle || statement.rfpSource || tBidQuality('categoryDetails.drawer.rfpSource')
          }
        />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold">
          {tBidQuality('categoryDetails.drawer.suggestion')}
        </h4>
        <MarkdownTextBlock sectionBody={formattedSuggestion} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold">{tBidQuality('categoryDetails.drawer.score')}</h4>
        <h4 className="text-sm py-1 italic">
          {tBidQuality('categoryDetails.drawer.scoreExplanation')}
        </h4>
        <p className="text-lg font-medium" style={{ color: scoreColor }}>
          {`${statement.score.toFixed(1)} ${tBidQuality(`assessmentLabels.${getAssessmentLabelKey(statement.score)}`)}`}
        </p>
      </div>

      {statement.changeProgress && (
        <div className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold">{tBidQuality('categoryDetails.drawer.changes')}</h4>
          <h4 className="text-sm font-semibold py-1">
            {tBidQuality('categoryDetails.drawer.changesExplanation')}
          </h4>
          <MarkdownTextBlock sectionBody={statement.changeProgress} />
        </div>
      )}

      <FeedbackCard
        isLoading={false}
        feedback={statement.isPositiveFeedback}
        resourceId={statement.id}
        type={FeedbackStatementTypeEnum.BidQualityStatement}
        queryKeysToInvalidate={[
          [queryKeys.BID_QUALITY_STATEMENT, bidId, statement.id],
          [queryKeys.BID_QUALITY_SECTION, bidId, sectionId],
        ]}
      />
    </div>
  );
}

function MarkdownTextBlock({
  sectionBody,
  sectionHeader,
  italicize,
  wrapperClassname = '',
}: {
  sectionHeader?: string;
  wrapperClassname?: string;
  sectionBody: string;
  italicize?: boolean;
}) {
  return (
    <article className={wrapperClassname}>
      {sectionHeader && <h4 className="text-xs font-bold">{sectionHeader}</h4>}
      <div className={cn(italicize && 'fst-italic', 'text-sm')}>
        <StyledMarkdown text={sectionBody} />
      </div>
    </article>
  );
}

function StyledMarkdown({
  text,
  className,
  highlight,
}: {
  text: string;
  className?: string;
  highlight?: boolean;
}) {
  const sanitizedText = text
    .replaceAll('<strong>', '**')
    .replaceAll('</strong>', '**')
    .replace(/<br\s*\/?>/gi, '\n');

  return (
    <ReactMarkdown
      className={cn('prose prose-sm max-w-none text-base text-neutral-900', className)}
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={{
        img() {
          // Images are not being loaded anyway, so we skip rendering them
          return null;
        },
        p(props) {
          return (
            <p className="mb-4 last:mb-0">
              {highlight ? (
                <span className="bg-textHighlight text-gray-950 ">{props.children}</span>
              ) : (
                props.children
              )}
            </p>
          );
        },
        strong(props) {
          return <strong className="font-semibold text-neutral-900">{props.children}</strong>;
        },
        em(props) {
          return <em className="italic">{props.children}</em>;
        },
        ul(props) {
          return <ul className="list-disc ml-0 mb-4 space-y-1">{props.children}</ul>;
        },
        ol(props) {
          return <ol className="list-decimal ml-0 mb-4 space-y-1">{props.children}</ol>;
        },
        li(props) {
          return (
            <li className="mb-1">
              {highlight ? (
                <span className="bg-textHighlight text-gray-950 ">{props.children}</span>
              ) : (
                props.children
              )}
            </li>
          );
        },
        table(props) {
          return (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full divide-y divide-neutral-200 border border-neutral-200">
                {props.children}
              </table>
            </div>
          );
        },
        thead(props) {
          return <thead className="bg-neutral-50">{props.children}</thead>;
        },
        tbody(props) {
          return <tbody className="divide-y divide-neutral-200 bg-white">{props.children}</tbody>;
        },
        tr(props) {
          return <tr className="hover:bg-neutral-50">{props.children}</tr>;
        },
        th(props) {
          return (
            <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
              {highlight ? (
                <span className="bg-textHighlight text-gray-950 ">{props.children}</span>
              ) : (
                props.children
              )}
            </th>
          );
        },
        td(props) {
          return (
            <td className="px-4 py-3 text-sm text-neutral-900 whitespace-pre-wrap">
              {highlight ? (
                <span className="bg-textHighlight text-gray-950 ">{props.children}</span>
              ) : (
                props.children
              )}
            </td>
          );
        },
      }}
    >
      {sanitizedText}
    </ReactMarkdown>
  );
}
